import * as React from "react";
import { Modal, Button } from "semantic-ui-react";
import { isRequestActive } from "redux-request-loading";
import { connect } from "react-redux";

import { ConfirmedButton } from "@teamprevent/common/confirmedButton/ConfirmedButton";
import { navigateToParentRoute } from "@teamprevent/common/routing/actions";

import { Clinic } from "@teamprevent/common/calendar/models";

import { ADMIN_ROLES, Authorize } from "../../../auth";
import { AdHocCalendarAppointmentModel, BookAppointmentErrors } from "../../../bookAppointments/models";
import { AdminUserInRedux } from "../../../crud/adminUser";
import { AdHocCalendarAppointmentForm } from "../../../bookAppointments/components/AdHocCalendarAppointmentForm";
import { ApplicationState } from "../../../applicationState";
import { UPDATE_TIME_NOTE_APPOINTMENT } from "../actionTypes";
import { updateTimeNoteAppointment } from "../actions";
import { CloneClinicModal } from "../../clinics/components/CloneClinicModal";
import { DELETE_APPOINTMENT } from "../../actionTypes";
import { deleteAppointment } from "../../actions";

interface TimeNoteAppointmentModalOwnProps {
    onModalClose: () => void;
    selectedAppointment: Clinic;
    isModalOpen: boolean;
}

interface StateProps {
    timeNoteId: string;
    adminUsers: AdminUserInRedux[];
    updatingAppointment: boolean;
    deletingAppointment: boolean;
    bookAppointmentErrors: BookAppointmentErrors | undefined;
    clinicId: string | undefined;
}

interface DispatchProps {
    save: (timeNoteId: string, clinicId: string | undefined, model: AdHocCalendarAppointmentModel) => Promise<void>;
    delete: (timeNoteId: string, clinicId?: string) => Promise<void>;
    navigateToParent: () => void;
}

interface MergedProps {
    onModalClose: () => void;
    selectedAppointment: Clinic;
    isModalOpen: boolean;
    adminUsers: AdminUserInRedux[];
    updatingAppointment: boolean;
    deletingAppointment: boolean;
    bookAppointmentErrors: BookAppointmentErrors | undefined;
    save: (model: AdHocCalendarAppointmentModel) => Promise<void>;
    delete: () => Promise<void>;
    navigateToParent: () => void;
}

interface State {
    cloning: boolean;
}

class TimeNoteAppointmentModalUnconnected extends React.Component<MergedProps, State> {
    constructor(props: MergedProps) {
        super(props);
        this.state = { cloning: false };
        this.onSubmit = this.onSubmit.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onToggleCloneClinicModal = this.onToggleCloneClinicModal.bind(this);
    }

    private bookForm: AdHocCalendarAppointmentForm | undefined;
    private getRef = (instance: AdHocCalendarAppointmentForm) => this.bookForm = instance;

    public render() {
        const appointmentDetails = this.props.selectedAppointment;

        return (
            <Modal open={this.props.isModalOpen} onClose={this.props.onModalClose} closeIcon>
                {this.state.cloning && <CloneClinicModal clinic={this.props.selectedAppointment} onClose={this.onToggleCloneClinicModal} />}
                <Modal.Content>
                    {this.renderCalendarForm(appointmentDetails)}
                </Modal.Content>
                <Modal.Actions>
                    <ConfirmedButton
                        icon="delete"
                        className="warning"
                        negative
                        content="Delete"
                        promptText="Are you sure you want to delete this time note?"
                        onClick={this.onDelete}
                        disabled={this.props.deletingAppointment}
                        loading={this.props.deletingAppointment}
                    />
                    <Authorize roles={[ADMIN_ROLES.SYSTEM_ADMIN, ADMIN_ROLES.SENIOR_ADMINISTRATOR, ADMIN_ROLES.ADMINISTRATOR]}>
                        <Button content="Clone" onClick={this.onToggleCloneClinicModal} />
                    </Authorize>
                    <Button onClick={this.props.onModalClose} content="Cancel" />
                    <Button
                        loading={this.props.updatingAppointment}
                        disabled={this.props.updatingAppointment}
                        onClick={this.onSubmit}
                        content="Save"
                    />
                </Modal.Actions>
            </Modal>
        );
    }

    private onToggleCloneClinicModal = () => this.setState({ cloning: !this.state.cloning });
    private onSubmit = () => this.bookForm?.submit();
    private onDelete = async () => {
        await this.props.delete();
        this.props.navigateToParent();
    };

    private renderCalendarForm = (appointmentDetails: Clinic) => {
        return (
            <AdHocCalendarAppointmentForm
                adminUsers={this.props.adminUsers}
                ref={this.getRef}
                initialValue={{
                    description: appointmentDetails?.description,
                    start: appointmentDetails?.start,
                    end: appointmentDetails?.end,
                    teamPreventUsers: this.props.selectedAppointment.teamPreventUsers ?? [],
                    bookingTypeId: appointmentDetails?.bookingTypeId
                }}
                bookAppointmentErrors={this.props.bookAppointmentErrors}
                save={this.props.save}
            />);
    };
}

const mapStateToProps = (state: ApplicationState): StateProps => ({
    timeNoteId: state.router.params?.appointmentId || "",
    clinicId: state.router.params?.clinicId,
    adminUsers: state.adminUsers,
    updatingAppointment: isRequestActive(state, UPDATE_TIME_NOTE_APPOINTMENT),
    bookAppointmentErrors: state.bookAppointments.bookAppointmentErrors,
    deletingAppointment: isRequestActive(state, DELETE_APPOINTMENT),
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
    save: (timeNoteId, clinicId,  model) => dispatch(updateTimeNoteAppointment(timeNoteId, clinicId, model)),
    delete: (timeNoteId, clinicId) => dispatch(deleteAppointment(timeNoteId, clinicId)),
    navigateToParent: () => dispatch(navigateToParentRoute())
});

const mergeProps = (stateProps: StateProps, dispatchProps: DispatchProps, ownProps: TimeNoteAppointmentModalOwnProps): MergedProps => {
    const { timeNoteId, clinicId, ...propsFromState } = stateProps;
    return {
        ...ownProps,
        ...propsFromState,
        save: (model) => dispatchProps.save(timeNoteId, clinicId, model),
        delete: () => dispatchProps.delete(timeNoteId, clinicId),
        navigateToParent: dispatchProps.navigateToParent,
    };
};

export const TimeNoteAppointmentModal = connect(mapStateToProps, mapDispatchToProps, mergeProps)(TimeNoteAppointmentModalUnconnected);
